// Onshore aquaculture infrastructure: Mattilsynet-approved fish slaughter /
// processing plants (named + geocoded; source /v1/onshore, backed by the
// slaughter_establishments register). Opt-in layer, a third map category
// alongside Anlegg and Fartøy.
//
// Brand-aligned ISOTYPE pins (per phoniq_brand.pdf — Deep #082E22 + Signal mint
// #B4D9C7): a flat Deep glyph on a mint marker — a fish for slaughter plants, a
// knife for processing/landing. Individual pins are sized by wellboat traffic;
// on zoom-out they cluster into mint bubbles sized by total traffic (mirroring
// the farm/biomass layer). Clicking a pin opens the plant page in Phoniq Insight.

import maplibregl from "maplibre-gl";

import { addPin, pinSVG } from "./pins";

const API_BASE = import.meta.env.VITE_API_BASE;
const IQ_BASE = "https://iq.phoniq.no";
const ONSHORE_LAYERS = ["onshore-pin"];
let popup: maplibregl.Popup | null = null;

// Brand palette: Deep is the ink/glyph, the mint family carries the kind. No
// off-brand amber/purple, and the Pulse red stays reserved.
const DEEP = "#082E22";
type OnshoreKind = "slaughter_farmed" | "slaughter_marine" | "processing";
export const ONSHORE_META: Record<OnshoreKind, { label: string; color: string }> = {
  slaughter_farmed: { label: "Lakseslakteri", color: "#B4D9C7" }, // Signal
  slaughter_marine: { label: "Slakteri (marin fisk)", color: "#7BE3C6" }, // bright mint
  processing: { label: "Tilvirkingsanlegg", color: "#8AA99A" }, // signal-dim
};

function esc(s: string | null): string {
  return (s ?? "").replace(
    /[&<>"]/g,
    (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" })[c] as string,
  );
}

// --- ISOTYPE pin glyphs (Deep ink on a mint marker) ------------------------

export function filletGlyph(ink: string, flesh: string): string {
  // Salmon fillet (ISOTYPE): a tapering teardrop of flesh, pointed at the tail
  // end, with pale myomere striations punched back to the marker fill. Centred
  // on the origin (pinSVG positions it).
  return (
    `<path d="M-12 -8 Q-15 0 -12 8 Q2 9.5 11 2 Q14.5 0 11 -2 Q2 -9.5 -12 -8 Z" fill="${ink}"/>` +
    `<g fill="none" stroke="${flesh}" stroke-width="1.4" stroke-linecap="round" opacity="0.9">` +
    `<path d="M-8.5 -4 Q-1 -3 7 -1"/>` +
    `<path d="M-9.5 0 Q-1 1 8.5 0"/>` +
    `<path d="M-8.5 4 Q-1 3 7 1"/>` +
    `</g>`
  );
}

function fishGlyph(ink: string, eye: string): string {
  // Flat fish swimming right: forked tail · dorsal fin · almond body · eye.
  // Centred on the origin (pinSVG positions it).
  return (
    `<path d="M-8 0 L-14 -6 L-11 0 L-14 6 Z" fill="${ink}"/>` +
    `<path d="M-9 -5 L-5 -9 L-3 -4 Z" fill="${ink}"/>` +
    `<path d="M-9 0 Q0 -7 10 -3 Q13 -1 13 0 Q13 1 10 3 Q0 7 -9 0 Z" fill="${ink}"/>` +
    `<circle cx="8" cy="-1.4" r="1.5" fill="${eye}"/>`
  );
}

function knifeGlyph(ink: string): string {
  return (
    `<g fill="${ink}">` +
    `<path d="M-11 9 L7 -9 Q10 -12 12 -10 L-9 11 Z"/>` +
    `<rect x="6" y="-12" width="9" height="4" rx="2" transform="rotate(-45 10.5 -10)"/>` +
    `</g>`
  );
}

export async function initOnshore(map: maplibregl.Map): Promise<void> {
  let data: GeoJSON.FeatureCollection = { type: "FeatureCollection", features: [] };
  try {
    const res = await fetch(`${API_BASE}/v1/onshore`, { cache: "no-store" });
    if (res.ok) data = (await res.json()) as GeoJSON.FeatureCollection;
  } catch {
    /* leave the layer empty if the feed is unavailable */
  }

  if (map.getSource("onshore")) {
    (map.getSource("onshore") as maplibregl.GeoJSONSource).setData(data);
    return;
  }

  // Solid mint token, Deep ring + Deep glyph (the eye punches back to the fill).
  const farmed = ONSHORE_META.slaughter_farmed.color;
  const marine = ONSHORE_META.slaughter_marine.color;
  const proc = ONSHORE_META.processing.color;
  await Promise.all([
    addPin(map, "pin-farmed", pinSVG(farmed, DEEP, filletGlyph(DEEP, farmed))),
    addPin(map, "pin-marine", pinSVG(marine, DEEP, fishGlyph(DEEP, marine))),
    addPin(map, "pin-processing", pinSVG(proc, DEEP, knifeGlyph(DEEP))),
  ]);

  // Cluster on zoom-out; the bubbles sum each plant's wellboat traffic.
  map.addSource("onshore", {
    type: "geojson",
    data,
    cluster: true,
    clusterRadius: 48,
    clusterMaxZoom: 9,
    // Full reduce form (operator + accumulator, then per-point map expression) —
    // the robust way to sum a property across a cluster.
    clusterProperties: {
      tsum: [["+", ["accumulated"], ["get", "tsum"]], ["coalesce", ["get", "traffic"], 0]],
    },
  });

  map.addLayer({
    id: "onshore-pin",
    type: "symbol",
    source: "onshore",
    filter: ["!", ["has", "point_count"]], // individual plants only; clusters are HTML bubbles
    layout: {
      visibility: "none", // opt-in, like the vessel layer
      "icon-image": [
        "match",
        ["get", "kind"],
        "slaughter_farmed", "pin-farmed",
        "slaughter_marine", "pin-marine",
        "pin-processing",
      ],
      // Size carries traffic: a quiet plant is small, a harvest hub is large.
      // Floored well above tiny so the glyph stays legible even at zero traffic.
      "icon-size": [
        "interpolate", ["linear"], ["coalesce", ["get", "traffic"], 0],
        0, 0.9, 15, 1.05, 50, 1.3, 120, 1.6,
      ],
      "icon-anchor": "center",
      "icon-allow-overlap": true,
      "icon-ignore-placement": true,
    },
  });

  map.on("click", "onshore-pin", (e) => {
    const f = e.features?.[0];
    if (!f) return;
    const p = f.properties as {
      id: string; name: string; poststed: string | null; kommune: string | null;
      prodform: string | null; kind: string; traffic: number; wellboats: number;
    };
    const coords = (f.geometry as GeoJSON.Point).coordinates.slice() as [number, number];
    const meta = ONSHORE_META[p.kind as OnshoreKind] ?? ONSHORE_META.processing;
    const traffic = p.wellboats
      ? `${p.wellboats} brønnbåt${p.wellboats === 1 ? "" : "er"} · ${p.traffic} kall (14 d)`
      : "";
    popup?.remove();
    popup = new maplibregl.Popup({ className: "onshore-tip", offset: 18, maxWidth: "260px" })
      .setLngLat(coords)
      .setHTML(
        `<div class="popup-label">${meta.label}</div>` +
          `<div class="onshore-name">${esc(p.name)}</div>` +
          `<div class="onshore-meta">${esc([p.poststed, p.kommune].filter(Boolean).join(" · "))}</div>` +
          (traffic ? `<div class="onshore-meta">${traffic}</div>` : "") +
          (p.prodform ? `<div class="onshore-meta">Produksjonsform: ${esc(p.prodform)}</div>` : "") +
          (p.id
            ? `<a class="onshore-link" href="${IQ_BASE}/p/${encodeURIComponent(p.id)}" target="_blank" rel="noopener">Åpne anleggsside →</a>`
            : ""),
      )
      .addTo(map);
  });
  map.on("mouseenter", "onshore-pin", () => (map.getCanvas().style.cursor = "pointer"));
  map.on("mouseleave", "onshore-pin", () => (map.getCanvas().style.cursor = ""));
}

export function setOnshoreVisible(map: maplibregl.Map, on: boolean): void {
  for (const id of ONSHORE_LAYERS) {
    if (map.getLayer(id)) map.setLayoutProperty(id, "visibility", on ? "visible" : "none");
  }
}
